import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, CheckCircle2, Settings, Loader2 } from 'lucide-react'
import { useMaintenanceMode } from '@/contexts/maintenance-mode-context'

export function MaintenanceModeManager() {
  const { isMaintenanceMode, loading, setMaintenanceMode } = useMaintenanceMode()
  const [saving, setSaving] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [statusType, setStatusType] = useState<'error' | 'success' | ''>('')

  const handleToggle = async (enabled: boolean) => {
    setSaving(true)
    setStatusMessage('')
    setStatusType('')

    const success = await setMaintenanceMode(enabled)

    if (success) {
      setStatusType('success')
      setStatusMessage(
        enabled
          ? 'Maintenance mode enabled. Site is now showing the Coming Soon page to all users (except admins).'
          : 'Maintenance mode disabled. Site is now accessible to all users.'
      )
    } else {
      setStatusType('error')
      setStatusMessage('Failed to update maintenance mode. Please try again.')
    }

    setSaving(false)

    // Clear message after 5 seconds
    setTimeout(() => {
      setStatusMessage('')
      setStatusType('')
    }, 5000)
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">
            Maintenance Mode
          </h2>
          <p className="text-slate-600 text-lg">
            Control site accessibility during upgrades or maintenance.
          </p>
        </div>
      </div>

      {statusMessage && (
        <Alert
          className={
            statusType === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : undefined
          }
          variant={statusType === 'error' ? 'destructive' : 'default'}
        >
          {statusType === 'success' ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>{statusMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Site Maintenance Control
          </CardTitle>
          <CardDescription>
            When enabled, all users (except admins) will see the Coming Soon page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-3" />
              Loading maintenance mode status...
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between p-6 rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04),inset_0_1px_0_rgba(255,255,255,0.8)]">
                <div className="space-y-1 flex-1">
                  <Label htmlFor="maintenance-mode" className="text-lg font-semibold text-slate-800">
                    Maintenance Mode
                  </Label>
                  <p className="text-sm text-slate-600">
                    {isMaintenanceMode
                      ? 'Site is currently in maintenance mode. Only admin users can access the site.'
                      : 'Site is currently accessible to all users.'}
                  </p>
                </div>
                <div className="flex items-center gap-4 ml-6">
                  <span className={`text-sm font-semibold transition-colors ${isMaintenanceMode ? 'text-slate-400' : 'text-slate-700'}`}>
                    Off
                  </span>
                  <div className="relative">
                    <div
                      className={`
                        relative w-20 h-10 rounded-full transition-all duration-300 ease-in-out cursor-pointer
                        ${saving ? 'opacity-50 cursor-not-allowed' : ''}
                        ${
                          isMaintenanceMode
                            ? 'bg-gradient-to-r from-orange-500 via-orange-400 to-red-500 shadow-[0_4px_15px_rgba(249,115,22,0.4)] border-2 border-orange-400'
                            : 'bg-gradient-to-br from-slate-200 to-slate-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.1)] border-2 border-slate-400'
                        }
                      `}
                      onClick={() => !saving && handleToggle(!isMaintenanceMode)}
                    >
                      <div
                        className={`
                          absolute top-1 left-1 bg-white w-8 h-8 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.15)]
                          transform transition-transform duration-300 ease-in-out
                          flex items-center justify-center
                          ${isMaintenanceMode ? 'translate-x-10' : 'translate-x-0'}
                        `}
                      >
                        {isMaintenanceMode && (
                          <span className="w-3 h-3 rounded-full bg-gradient-to-r from-orange-500 to-red-500"></span>
                        )}
                      </div>
                    </div>
                    <Switch
                      id="maintenance-mode"
                      checked={isMaintenanceMode}
                      onCheckedChange={handleToggle}
                      disabled={saving}
                      className="sr-only"
                      aria-hidden="true"
                    />
                  </div>
                  <span className={`text-sm font-semibold transition-colors ${isMaintenanceMode ? 'text-orange-600' : 'text-slate-400'}`}>
                    On
                  </span>
                </div>
              </div>

              <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-900">How it works:</h3>
                <ul className="space-y-2 text-sm text-blue-800 list-disc list-inside">
                  <li>When enabled, all routes (except /admin and /admin-login) will show the Coming Soon page</li>
                  <li>Admin users can still access the admin dashboard during maintenance</li>
                  <li>Regular users trying to access any page will see the Coming Soon page</li>
                  <li>Changes take effect immediately</li>
                </ul>
              </div>

              {isMaintenanceMode && (
                <Alert className="border-amber-200 bg-amber-50 text-amber-700">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Maintenance mode is active.</strong> All public pages are showing the Coming Soon page.
                    Disable maintenance mode when you're ready to make the site accessible again.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

